import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  type CreateVenueBlackoutWindowInput,
  type ProviderCurrentSession,
  type ProviderSession,
  providerApi,
  workspaceApi,
  type CreateUnitInput,
  type CreateVenueInput,
  type ProviderUnit,
  type ProviderVenue,
  type ProviderVenuesResponse,
  type VenueOperatingHoursEntry,
  type WorkspaceSummary,
  isOfflineApiError,
} from "../../lib/api";
import {
  subscribeRealtimeConnection,
  subscribeRealtimeVersion,
} from "../../lib/realtime";
import {
  createOfflineRecordId,
  writeCachedApiResponse,
} from "../../lib/offline-store";
import { useProviderOffline } from "../../lib/provider-offline";
import { useAppPreferences } from "../../lib/preferences";
import { getFallbackWorkspaceSummary } from "../workspace/content";

function getRequestErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.detail;
  }

  return fallback;
}

function resolveSelectedVenueId(
  venues: ProviderVenue[],
  preferredVenueId: string | null,
) {
  if (preferredVenueId && venues.some((venue) => venue.id === preferredVenueId)) {
    return preferredVenueId;
  }

  return venues[0]?.id ?? null;
}

function resolveSelectedUnitId(
  units: ProviderUnit[],
  preferredUnitId: string | null,
) {
  if (preferredUnitId && units.some((unit) => unit.id === preferredUnitId)) {
    return preferredUnitId;
  }

  return units[0]?.id ?? null;
}

function createDefaultOperatingHours(): VenueOperatingHoursEntry[] {
  return Array.from({ length: 7 }, (_, day_of_week) => ({
    day_of_week,
    is_open: day_of_week !== 5 && day_of_week !== 6,
    open_time: day_of_week !== 5 && day_of_week !== 6 ? "10:00" : null,
    close_time: day_of_week !== 5 && day_of_week !== 6 ? "23:00" : null,
  }));
}

function toCurrentSession(session: ProviderSession): ProviderCurrentSession {
  return {
    id: session.id,
    booking_id: session.booking_id,
    source: session.source,
    status: session.status,
    customer_name: session.customer_name,
    started_at: session.started_at,
    planned_end_at: session.planned_end_at,
    ended_at: session.ended_at,
    duration_minutes: session.duration_minutes,
    overtime_minutes: session.overtime_minutes,
    is_overtime: session.is_overtime,
  };
}

const PROVIDER_VENUES_PATH = "/api/v1/provider/venues";
const recentProviderVenueStorageKey = "gaamed.provider.recentVenueId";

function readRecentProviderVenueId() {
  return localStorage.getItem(recentProviderVenueStorageKey);
}

function writeRecentProviderVenueId(venueId: string | null) {
  if (venueId) {
    localStorage.setItem(recentProviderVenueStorageKey, venueId);
    return;
  }

  localStorage.removeItem(recentProviderVenueStorageKey);
}

export function useProviderWorkspace() {
  const { t } = useAppPreferences();
  const { queueMutation, syncVersion } = useProviderOffline();
  const [summary, setSummary] = useState<WorkspaceSummary>(
    getFallbackWorkspaceSummary("provider"),
  );
  const [workspace, setWorkspace] = useState<ProviderVenuesResponse | null>(null);
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creatingVenue, setCreatingVenue] = useState(false);
  const [updatingVenue, setUpdatingVenue] = useState(false);
  const [deletingVenue, setDeletingVenue] = useState(false);
  const [creatingUnit, setCreatingUnit] = useState(false);
  const [updatingUnit, setUpdatingUnit] = useState(false);
  const [deletingUnit, setDeletingUnit] = useState(false);
  const [updatingAvailability, setUpdatingAvailability] = useState(false);
  const [creatingBlackoutWindow, setCreatingBlackoutWindow] = useState(false);
  const [deletingBlackoutWindowId, setDeletingBlackoutWindowId] = useState<string | null>(null);
  const workspaceRef = useRef<ProviderVenuesResponse | null>(null);
  const selectedVenueIdRef = useRef<string | null>(null);
  const selectedUnitIdRef = useRef<string | null>(null);
  const reloadGenerationRef = useRef(0);

  useEffect(() => () => { reloadGenerationRef.current += 1; }, []);

  useEffect(() => {
    workspaceRef.current = workspace;
  }, [workspace]);

  useEffect(() => {
    selectedVenueIdRef.current = selectedVenueId;
    if (workspaceRef.current !== null) {
      writeRecentProviderVenueId(selectedVenueId);
    }
  }, [selectedVenueId]);

  useEffect(() => {
    selectedUnitIdRef.current = selectedUnitId;
  }, [selectedUnitId]);

  const persistWorkspace = useCallback((nextWorkspace: ProviderVenuesResponse | null) => {
    // Removed venues remain in historical records, but cannot be selected or
    // configured in the active provider workspace, including from stale caches.
    const activeWorkspace = nextWorkspace
      ? { ...nextWorkspace, venues: nextWorkspace.venues.filter((venue) => venue.approval_status !== "removed") }
      : null;
    workspaceRef.current = activeWorkspace;
    setWorkspace(activeWorkspace);
    if (activeWorkspace) {
      writeCachedApiResponse(PROVIDER_VENUES_PATH, activeWorkspace);
    }
    return activeWorkspace;
  }, []);

  const reload = useCallback(
    async (preferredVenueId?: string | null, preferredUnitId?: string | null) => {
      const generation = ++reloadGenerationRef.current;
      setIsLoading(true);
      setLoadError(null);

      try {
        const nextSummary = await workspaceApi.providerSummary();
        if (generation !== reloadGenerationRef.current) return;
        setSummary(nextSummary);
        if (nextSummary.provider_status !== "approved") {
          persistWorkspace(null);
          setSelectedVenueId(null);
          setSelectedUnitId(null);
          writeRecentProviderVenueId(null);
          return;
        }

        const venueResponse = await providerApi.listVenues({ skipOfflineCacheWrite: true });
        if (generation !== reloadGenerationRef.current) return;
        const nextWorkspace = persistWorkspace(venueResponse);
        if (!nextWorkspace) return;
        const resolvedVenueId = resolveSelectedVenueId(
          nextWorkspace.venues,
          preferredVenueId ?? selectedVenueIdRef.current ?? readRecentProviderVenueId(),
        );
        const resolvedVenue =
          nextWorkspace.venues.find((venue) => venue.id === resolvedVenueId) ?? null;
        const resolvedUnitId = resolveSelectedUnitId(
          resolvedVenue?.units ?? [],
          preferredUnitId ?? selectedUnitIdRef.current,
        );

        setSelectedVenueId(resolvedVenueId);
        setSelectedUnitId(resolvedUnitId);
        // A null-to-null state update does not run the persistence effect, so
        // clear a saved removed venue even when no active venues remain.
        writeRecentProviderVenueId(resolvedVenueId);
      } catch (error) {
        if (generation === reloadGenerationRef.current) {
          setLoadError(getRequestErrorMessage(error, t("Unable to load provider workspace.")));
        }
      } finally {
        if (generation === reloadGenerationRef.current) setIsLoading(false);
      }
    },
    [persistWorkspace, t],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (syncVersion === 0) {
      return;
    }

    void reload();
  }, [reload, syncVersion]);

  // Reload when realtime push arrives.
  useEffect(() => {
    return subscribeRealtimeVersion(() => {
      void reload();
    });
  }, [reload]);

  // Reconnects can follow a period in which this tab missed events. Fetch
  // the authoritative workspace snapshot whenever the stream opens again.
  useEffect(() => {
    return subscribeRealtimeConnection((isConnected) => {
      if (isConnected) {
        void reload();
      }
    });
  }, [reload]);

  // Keep a low-frequency reconciliation even while SSE is connected: an open
  // socket does not guarantee that every event reached this tab.
  useEffect(() => {
    const intervalId = setInterval(() => {
      void reload();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [reload]);

  const createVenue = useCallback(
    async (payload: CreateVenueInput) => {
      setCreatingVenue(true);
      setSaveError(null);
      setNotice(null);

      try {
        const venue = await providerApi.createVenue(payload);
        await reload(venue.id);
        setNotice(t("Sent {name} to admin for venue approval.", { name: venue.name }));
        return venue;
      } catch (error) {
        if (isOfflineApiError(error)) {
          const currentWorkspace = workspaceRef.current;
          if (!currentWorkspace) {
            setSaveError(t("Provider workspace is not cached for offline venue creation."));
            return null;
          }

          const venue = {
            id: createOfflineRecordId("venue"),
            name: payload.name,
            city: payload.city,
            timezone: payload.timezone,
            address: payload.address,
            image_url: payload.image_url ?? null,
            logo_url: payload.logo_url ?? null,
            latitude: payload.latitude ?? null,
            longitude: payload.longitude ?? null,
            approval_status: "pending" as const,
            submission_note: payload.submission_note ?? null,
            removal_note: null,
            review_note: null,
            reviewed_at: null,
            is_active: false,
            photos: [],
            operating_hours: createDefaultOperatingHours(),
            blackout_windows: [],
            units: [],
          };
          persistWorkspace({
            ...currentWorkspace,
            venues: [...currentWorkspace.venues, venue],
          });
          setSelectedVenueId(venue.id);
          setSelectedUnitId(null);
          queueMutation({
            method: "POST",
            path: PROVIDER_VENUES_PATH,
            body: payload,
            label: `Create venue ${payload.name}`,
            local_resource_id: venue.id,
          });
          setNotice(t("Saved venue request for {name} offline. It will sync when connection returns.", { name: payload.name }));
          return venue;
        }

        const detail = getRequestErrorMessage(
          error,
          t("Unable to create venue."),
        );
        setSaveError(detail);
      } finally {
        setCreatingVenue(false);
      }
    },
    [reload, t],
  );

  const updateVenue = useCallback(
    async (venueId: string, payload: CreateVenueInput) => {
      setUpdatingVenue(true);
      setSaveError(null);
      setNotice(null);

      try {
        const venue = await providerApi.updateVenue(venueId, payload);
        await reload(venue.id, selectedUnitIdRef.current);
        setNotice(t("Updated venue {name}.", { name: venue.name }));
        return venue;
      } catch (error) {
        if (isOfflineApiError(error)) {
          const currentWorkspace = workspaceRef.current;
          if (!currentWorkspace) {
            setSaveError(t("Provider workspace is not cached for offline venue updates."));
            return null;
          }

          persistWorkspace({
            ...currentWorkspace,
            venues: currentWorkspace.venues.map((venue) =>
              venue.id === venueId
                ? {
                    ...venue,
                    name: payload.name,
                    city: payload.city,
                    timezone: payload.timezone,
                    address: payload.address,
                    image_url: payload.image_url ?? null,
                    logo_url: payload.logo_url ?? null,
                    latitude: payload.latitude !== undefined ? payload.latitude : venue.latitude,
                    longitude: payload.longitude !== undefined ? payload.longitude : venue.longitude,
                  }
                : venue,
            ),
          });
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/venues/${venueId}`,
            body: payload,
            label: `Update venue ${payload.name}`,
          });
          setNotice(t("Saved venue {name} offline. It will sync when connection returns.", { name: payload.name }));
          return currentWorkspace.venues.find((venue) => venue.id === venueId) ?? null;
        }

        const detail = getRequestErrorMessage(error, t("Unable to update venue."));
        setSaveError(detail);
      } finally {
        setUpdatingVenue(false);
      }
    },
    [reload, t],
  );

  const deleteVenue = useCallback(
    async (venueId: string, removalNote: string) => {
      setDeletingVenue(true);
      setSaveError(null);
      setNotice(null);

      try {
        const venue = await providerApi.requestVenueRemoval(venueId, {
          removal_note: removalNote,
        });
        await reload(venue.id, selectedUnitIdRef.current);
        setNotice(t("Sent removal request for {name}. Admin approval is required.", { name: venue.name }));
      } catch (error) {
        if (isOfflineApiError(error)) {
          const currentWorkspace = workspaceRef.current;
          if (!currentWorkspace) {
            setSaveError(t("Provider workspace is not cached for offline venue removal requests."));
            return;
          }

          persistWorkspace({
            ...currentWorkspace,
            venues: currentWorkspace.venues.map((venue) =>
              venue.id === venueId
                ? {
                    ...venue,
                    approval_status: "removal_requested" as const,
                    removal_note: removalNote,
                    review_note: null,
                    reviewed_at: null,
                  }
                : venue,
            ),
          });
          queueMutation({
            method: "POST",
            path: `/api/v1/provider/venues/${venueId}/removal-request`,
            body: { removal_note: removalNote },
            label: "Request venue removal",
          });
          setNotice(t("Saved removal request offline. It will sync when connection returns."));
          return;
        }

        const detail = getRequestErrorMessage(error, t("Unable to request venue removal."));
        setSaveError(detail);
      } finally {
        setDeletingVenue(false);
      }
    },
    [reload, t],
  );

  const createUnit = useCallback(
    async (venueId: string, payload: CreateUnitInput) => {
      setCreatingUnit(true);
      setSaveError(null);
      setNotice(null);

      try {
        const unit = await providerApi.createUnit(venueId, payload);
        await reload(venueId, unit.id);
        setNotice(t("Created unit {name}.", { name: unit.name }));
        return unit;
      } catch (error) {
        if (isOfflineApiError(error)) {
          const currentWorkspace = workspaceRef.current;
          const venue = currentWorkspace?.venues.find((entry) => entry.id === venueId) ?? null;
          if (!currentWorkspace || !venue) {
            setSaveError(t("Provider workspace is not cached for offline unit creation."));
            return null;
          }

          const unit: ProviderUnit = {
            id: createOfflineRecordId("unit"),
            name: payload.name,
            type: payload.type,
            status: payload.status,
            base_status: payload.status,
            base_hourly_rate: payload.base_hourly_rate,
            capacity: payload.capacity,
            is_vip: payload.is_vip,
            photos: [],
            peak_pricing_windows: payload.peak_pricing_windows.map((window) => ({
              id: createOfflineRecordId("peak-pricing"),
              day_of_week: window.day_of_week,
              start_time: window.start_time,
              end_time: window.end_time,
              hourly_rate: window.hourly_rate,
            })),
            current_session: null,
          };
          persistWorkspace({
            ...currentWorkspace,
            venues: currentWorkspace.venues.map((entry) =>
              entry.id === venueId ? { ...entry, units: [...entry.units, unit] } : entry,
            ),
          });
          setSelectedVenueId(venueId);
          setSelectedUnitId(unit.id);
          queueMutation({
            method: "POST",
            path: `/api/v1/provider/venues/${venueId}/units`,
            body: payload,
            label: `Create unit ${payload.name}`,
            local_resource_id: unit.id,
          });
          setNotice(t("Saved unit {name} offline. It will sync when connection returns.", { name: payload.name }));
          return unit;
        }

        const detail = getRequestErrorMessage(error, t("Unable to create unit."));
        setSaveError(detail);
      } finally {
        setCreatingUnit(false);
      }
    },
    [reload, t],
  );

  const updateUnit = useCallback(
    async (venueId: string, unitId: string, payload: CreateUnitInput) => {
      setUpdatingUnit(true);
      setSaveError(null);
      setNotice(null);

      try {
        const unit = await providerApi.updateUnit(venueId, unitId, payload);
        await reload(venueId, unit.id);
        setNotice(t("Updated unit {name}.", { name: unit.name }));
        return unit;
      } catch (error) {
        if (isOfflineApiError(error)) {
          const currentWorkspace = workspaceRef.current;
          if (!currentWorkspace) {
            setSaveError(t("Provider workspace is not cached for offline unit updates."));
            return null;
          }

          persistWorkspace({
            ...currentWorkspace,
            venues: currentWorkspace.venues.map((venue) =>
              venue.id === venueId
                ? {
                    ...venue,
                    units: venue.units.map((unit) =>
                      unit.id === unitId
                        ? {
                            ...unit,
                            name: payload.name,
                            type: payload.type,
                            status:
                              unit.current_session === null ? payload.status : "occupied",
                            base_status: payload.status,
                            base_hourly_rate: payload.base_hourly_rate,
                            capacity: payload.capacity,
                            is_vip: payload.is_vip,
                            peak_pricing_windows: payload.peak_pricing_windows.map((window) => ({
                              id: createOfflineRecordId("peak-pricing"),
                              day_of_week: window.day_of_week,
                              start_time: window.start_time,
                              end_time: window.end_time,
                              hourly_rate: window.hourly_rate,
                            })),
                          }
                        : unit,
                    ),
                  }
                : venue,
            ),
          });
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/venues/${venueId}/units/${unitId}`,
            body: payload,
            label: `Update unit ${payload.name}`,
          });
          setNotice(t("Saved unit {name} offline. It will sync when connection returns.", { name: payload.name }));
          return null;
        }

        const detail = getRequestErrorMessage(error, t("Unable to update unit."));
        setSaveError(detail);
      } finally {
        setUpdatingUnit(false);
      }
    },
    [reload, t],
  );

  const deleteUnit = useCallback(
    async (venueId: string, unitId: string) => {
      setDeletingUnit(true);
      setSaveError(null);
      setNotice(null);

      try {
        await providerApi.deleteUnit(venueId, unitId);
        await reload(venueId, null);
        setNotice(t("Deleted unit."));
      } catch (error) {
        if (isOfflineApiError(error)) {
          const currentWorkspace = workspaceRef.current;
          const venue = currentWorkspace?.venues.find((entry) => entry.id === venueId) ?? null;
          if (!currentWorkspace || !venue) {
            setSaveError(t("Provider workspace is not cached for offline unit deletion."));
            return;
          }

          const nextUnits = venue.units.filter((unit) => unit.id !== unitId);
          persistWorkspace({
            ...currentWorkspace,
            venues: currentWorkspace.venues.map((entry) =>
              entry.id === venueId ? { ...entry, units: nextUnits } : entry,
            ),
          });
          setSelectedVenueId(venueId);
          setSelectedUnitId(resolveSelectedUnitId(nextUnits, null));
          queueMutation({
            method: "DELETE",
            path: `/api/v1/provider/venues/${venueId}/units/${unitId}`,
            body: null,
            label: "Delete unit",
          });
          setNotice(t("Deleted unit offline. It will sync when connection returns."));
          return;
        }

        const detail = getRequestErrorMessage(error, t("Unable to delete unit."));
        setSaveError(detail);
      } finally {
        setDeletingUnit(false);
      }
    },
    [reload, t],
  );

  const updateAvailability = useCallback(
    async (venueId: string, operatingHours: VenueOperatingHoursEntry[]) => {
      setUpdatingAvailability(true);
      setSaveError(null);
      setNotice(null);

      try {
        const venue = await providerApi.updateVenueAvailability(venueId, {
          operating_hours: operatingHours,
        });
        await reload(venue.id, selectedUnitIdRef.current);
        setNotice(t("Updated availability for {name}.", { name: venue.name }));
      } catch (error) {
        if (isOfflineApiError(error)) {
          const currentWorkspace = workspaceRef.current;
          if (!currentWorkspace) {
            setSaveError(
              t("Provider workspace is not cached for offline availability updates."),
            );
            return;
          }

          const venue = currentWorkspace.venues.find((entry) => entry.id === venueId) ?? null;
          persistWorkspace({
            ...currentWorkspace,
            venues: currentWorkspace.venues.map((entry) =>
              entry.id === venueId ? { ...entry, operating_hours: operatingHours } : entry,
            ),
          });
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/venues/${venueId}/availability`,
            body: {
              operating_hours: operatingHours,
            },
            label: `Update availability for ${venue?.name ?? "venue"}`,
          });
          setNotice(
            t("Saved availability for {name} offline. It will sync when connection returns.", { name: venue?.name ?? t("venue") }),
          );
          return;
        }

        const detail = getRequestErrorMessage(
          error,
          t("Unable to update venue availability."),
        );
        setSaveError(detail);
      } finally {
        setUpdatingAvailability(false);
      }
    },
    [reload, t],
  );

  const createBlackoutWindow = useCallback(
    async (venueId: string, payload: CreateVenueBlackoutWindowInput) => {
      setCreatingBlackoutWindow(true);
      setSaveError(null);
      setNotice(null);

      try {
        const venue = await providerApi.createVenueBlackoutWindow(venueId, payload);
        await reload(venue.id, selectedUnitIdRef.current);
        setNotice(t("Added blackout window for {name}.", { name: venue.name }));
        return venue;
      } catch (error) {
        const detail = getRequestErrorMessage(
          error,
          t("Unable to create blackout window."),
        );
        setSaveError(detail);
        return null;
      } finally {
        setCreatingBlackoutWindow(false);
      }
    },
    [reload, t],
  );

  const deleteBlackoutWindow = useCallback(
    async (venueId: string, blackoutId: string) => {
      setDeletingBlackoutWindowId(blackoutId);
      setSaveError(null);
      setNotice(null);

      try {
        const venue = await providerApi.deleteVenueBlackoutWindow(venueId, blackoutId);
        await reload(venue.id, selectedUnitIdRef.current);
        setNotice(t("Removed blackout window for {name}.", { name: venue.name }));
        return venue;
      } catch (error) {
        const detail = getRequestErrorMessage(
          error,
          t("Unable to delete blackout window."),
        );
        setSaveError(detail);
        return null;
      } finally {
        setDeletingBlackoutWindowId(null);
      }
    },
    [reload, t],
  );

  const venues = workspace?.venues ?? [];
  const selectedVenue = useMemo(
    () => venues.find((venue) => venue.id === selectedVenueId) ?? venues[0] ?? null,
    [selectedVenueId, venues],
  );
  const selectedUnit = useMemo(
    () =>
      selectedVenue?.units.find((unit) => unit.id === selectedUnitId) ??
      selectedVenue?.units[0] ??
      null,
    [selectedUnitId, selectedVenue],
  );

  const applyOfflineSessionStart = useCallback(
    (session: ProviderSession) => {
      const currentWorkspace = workspaceRef.current;
      if (!currentWorkspace) {
        return;
      }

      persistWorkspace({
        ...currentWorkspace,
        venues: currentWorkspace.venues.map((venue) =>
          venue.id === session.venue_id
            ? {
                ...venue,
                units: venue.units.map((unit) =>
                  unit.id === session.unit_id
                    ? {
                        ...unit,
                        status: "occupied",
                        current_session: toCurrentSession(session),
                      }
                    : unit,
                ),
              }
            : venue,
        ),
      });
    },
    [persistWorkspace],
  );

  const applyOfflineSessionUpdate = useCallback(
    (session: ProviderSession) => {
      const currentWorkspace = workspaceRef.current;
      if (!currentWorkspace) {
        return;
      }

      persistWorkspace({
        ...currentWorkspace,
        venues: currentWorkspace.venues.map((venue) =>
          venue.id === session.venue_id
            ? {
                ...venue,
                units: venue.units.map((unit) =>
                  unit.id === session.unit_id &&
                  unit.current_session?.id === session.id
                    ? {
                        ...unit,
                        current_session: toCurrentSession(session),
                      }
                    : unit,
                ),
              }
            : venue,
        ),
      });
    },
    [persistWorkspace],
  );

  const applyOfflineSessionStop = useCallback(
    (session: ProviderSession) => {
      const currentWorkspace = workspaceRef.current;
      if (!currentWorkspace) {
        return;
      }

      persistWorkspace({
        ...currentWorkspace,
        venues: currentWorkspace.venues.map((venue) =>
          venue.id === session.venue_id
            ? {
                ...venue,
                units: venue.units.map((unit) =>
                  unit.id === session.unit_id
                    ? {
                        ...unit,
                        status: unit.base_status === "occupied" ? "available" : unit.base_status,
                        current_session: null,
                      }
                    : unit,
                ),
              }
            : venue,
        ),
      });
    },
    [persistWorkspace],
  );

  return {
    summary,
    provider: workspace?.provider ?? null,
    venues,
    selectedVenue,
    selectedUnit,
    selectedVenueId,
    selectedUnitId,
    setSelectedVenueId,
    setSelectedUnitId,
    isLoading,
    loadError,
    saveError,
    notice,
    creatingVenue,
    updatingVenue,
    deletingVenue,
    creatingUnit,
    updatingUnit,
    deletingUnit,
    updatingAvailability,
    creatingBlackoutWindow,
    deletingBlackoutWindowId,
    createVenue,
    updateVenue,
    deleteVenue,
    createUnit,
    updateUnit,
    deleteUnit,
    updateAvailability,
    createBlackoutWindow,
    deleteBlackoutWindow,
    applyOfflineSessionStart,
    applyOfflineSessionUpdate,
    applyOfflineSessionStop,
    reload,
  };
}
